import React from 'react';
import { Table, TableBody, TableContainer } from '@mui/material';
import PackMonitoringHeader from './PackMonitoringTableHeaders';
import PackItemRow from './PackItemRow';
import PhysicianRow from './PhysicianRow';
import CategoryRow from './CategoryRow';
import { useMemo } from 'react';
import { useTableSort, sortData } from '../../../utils/tableSorting';

function PackMonitoringTable({ data, tableType, selectedItemId, onItemSelect, viewMode = 'standard' }) {
    const defaultOrder = tableType === 'physicians' ? 'desc' : 'asc';
    const defaultOrderBy = tableType === 'items' ? 'item_name' : 'pack_usage';
    
    const { order, orderBy, handleRequestSort } = useTableSort(defaultOrderBy, defaultOrder);

    // Convert data to array format for sorting
    const dataArray = useMemo(() => {
        if (Array.isArray(data)) {
            return data;
        }
        return Object.values(data);
    }, [data]);

    const sortedData = useMemo(() => {
        return sortData(dataArray, order, orderBy);
    }, [order, orderBy, dataArray]);

    // Group items by category for the items table
    const groupedItems = useMemo(() => {
        if (tableType !== 'items') return null;
        const map = new Map();
        for (const item of sortedData) {
            const cat = item.category || 'uncategorized';
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat).push(item);
        }
        return Array.from(map.entries())
            .map(([category, items]) => ({ category, items }))
            .sort((a, b) => (a.category ?? '').localeCompare(b.category ?? ''));
    }, [tableType, sortedData]);

    if (!data || (Array.isArray(data) && data.length === 0) || (typeof data === 'object' && Object.keys(data).length === 0)) {
        return null;
    }

    return (
        <TableContainer sx={{ 
            borderRadius: '8px', 
            boxShadow: 6, 
            backgroundColor: 'white',
            maxHeight: '70vh',
            overflowY: 'auto',
            height: 'auto'
        }}>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }} stickyHeader>
                <PackMonitoringHeader
                    tableType={tableType}
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    selectedItemId={selectedItemId}
                    viewMode={viewMode}
                />
                <TableBody>
                    {tableType === 'items'
                        ? groupedItems.map(({ category, items }) => (
                            <React.Fragment key={category}>
                                <CategoryRow category={category} />
                                {items.map((item) => (
                                    <PackItemRow
                                        key={item.item_id}
                                        item={item}
                                        isSelected={selectedItemId === item.item_id}
                                        onSelect={() => onItemSelect(item.item_id)}
                                        viewMode={viewMode}
                                    />
                                ))}
                            </React.Fragment>
                        ))
                        : sortedData.map((item) => (
                            <PhysicianRow
                                key={item.id}
                                physician={item}
                                selectedItemId={selectedItemId}
                                viewMode={viewMode}
                            />
                        ))
                    }
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default PackMonitoringTable;